// React Dependencies
import React, { useState, useEffect } from 'react';

// External Dependencies
import _ from 'lodash';
import PropTypes from 'prop-types';
import { DndContext, MouseSensor, TouchSensor, useSensor, useSensors } from '@dnd-kit/core';
import { SortableContext, rectSortingStrategy, verticalListSortingStrategy, arrayMove } from '@dnd-kit/sortable';
import { restrictToFirstScrollableAncestor, restrictToVerticalAxis } from '@dnd-kit/modifiers';
import { isMobile } from 'react-device-detect';

// Internal Dependencies
import SortableMediaItem from './SortableMediaItem';
import { IsEmpty } from '../../../common/checks';

const SortableMedia = ({
  maxHt, minHt, maxWd, minWd, pdf, images, listItemMargin, handleCrop, handleView,
  handleDelete, handleSort, handleUploadPdf, handleDeletePdf, iconContainerStyles,
  isCropIconVisible, isViewIconVisible, isDeleteIconVisible, disabled, sortable,
}) => {
  const [items, setItems] = useState([]);

  const sensors = useSensors(
    useSensor(TouchSensor),
    useSensor(MouseSensor),
  );

  const handleDragEnd = (event) => {
    const { active, over } = event;

    if (IsEmpty(active) || IsEmpty(over)) {
      return;
    }

    if (active.id !== over.id) {
      setItems((_items) => {
        const oldIndex = _items.findIndex((item) => item.id === active.id);
        const newIndex = _items.findIndex((item) => item.id === over.id);

        const sorted = arrayMove(_items, oldIndex, newIndex);
        handleSort(sorted);

        return sorted;
      });
    }
  };

  useEffect(() => {
    const copyImages = _.cloneDeep(images);
    const formattedImages = _.map(copyImages, (image, index) => ({ id: `${new Date().getTime()}-index-${index + 1}`, ...image }));

    setItems(formattedImages);
  }, [images]);

  return (
    <DndContext onDragEnd={handleDragEnd} sensors={sensors} modifiers={[isMobile ? restrictToVerticalAxis : restrictToFirstScrollableAncestor]}>
      <SortableContext
        items={items}
        disabled={disabled || !sortable}
        strategy={isMobile ? verticalListSortingStrategy : rectSortingStrategy}
      >
        <div style={{ display: 'flex', flexDirection: isMobile ? 'column' : 'row', flexWrap: 'wrap' }}>
          {items.map((item) => (
            <SortableMediaItem
              key={item.id}
              item={item}
              maxHt={maxHt}
              minHt={minHt}
              maxWd={maxWd}
              minWd={minWd}
              handleCrop={handleCrop}
              handleView={handleView}
              handleDelete={handleDelete}
              handleSort={handleSort}
              handleUploadPdf={handleUploadPdf}
              handleDeletePdf={handleDeletePdf}
              listItemMargin={listItemMargin}
              pdf={pdf}
              iconContainerStyles={iconContainerStyles}
              isCropIconVisible={IsEmpty(item.isCroppable) ? isCropIconVisible : item.isCroppable}
              isViewIconVisible={IsEmpty(item.isViewable) ? isViewIconVisible : item.isViewable}
              isDeleteIconVisible={IsEmpty(item.isDeletable) ? isDeleteIconVisible : item.isDeletable}
              disabled={disabled}
            />
          ))}
        </div>
      </SortableContext>
    </DndContext>
  );
};

export default SortableMedia;

SortableMedia.propTypes = {
  maxHt:               PropTypes.number,
  minHt:               PropTypes.number,
  maxWd:               PropTypes.number,
  minWd:               PropTypes.number,
  images:              PropTypes.arrayOf(PropTypes.shape(Object)),
  listItemMargin:      PropTypes.string,
  iconContainerStyles: PropTypes.shape(Object),
  pdf:                 PropTypes.bool,
  isCropIconVisible:   PropTypes.bool,
  isViewIconVisible:   PropTypes.bool,
  isDeleteIconVisible: PropTypes.bool,
  disabled:            PropTypes.bool,
  sortable:            PropTypes.bool,
  handleSort:          PropTypes.func,
  handleCrop:          PropTypes.func,
  handleDelete:        PropTypes.func,
  handleView:          PropTypes.func,
  handleUploadPdf:     PropTypes.func,
  handleDeletePdf:     PropTypes.func,
};

SortableMedia.defaultProps = {
  maxHt:               0,
  minHt:               0,
  maxWd:               0,
  minWd:               0,
  images:              [],
  listItemMargin:      null,
  iconContainerStyles: {},
  pdf:                 false,
  isCropIconVisible:   true,
  isViewIconVisible:   true,
  isDeleteIconVisible: true,
  disabled:            false,
  sortable:            true,
  handleSort:          () => { },
  handleCrop:          () => { },
  handleDelete:        () => { },
  handleView:          () => { },
  handleUploadPdf:     () => { },
  handleDeletePdf:     () => { },
};
